import React, {
    useCallback, useContext, useEffect, useRef, useState
} from 'react';
import { createPortal } from 'react-dom';
import { useAIChat } from '../../contexts/AIChat';
import { UserContext } from '../../contexts/User';
import { ActiveFarmContext } from '../../contexts/ActiveFarm';
import useMediaQuery from '../../customHooks/useMediaQuery';
import AIChatMessages from './AIChatMessages';
import AIChatComposer from './AIChatComposer';
import { canUseAIChat, MOBILE_MEDIA_QUERY } from './constants';

import './style.scss';

const WIDTH_STORAGE_KEY = 'zelpAiDrawerWidth';
const DEFAULT_WIDTH_REM = 48; // keep in sync with .ai-chat width in style.scss
const MIN_WIDTH_REM = 24;
const MAX_WIDTH_REM = 64;
const KEYBOARD_RESIZE_STEP = 16;

const remToPx = rem => rem * parseFloat(getComputedStyle(document.documentElement).fontSize || 16);

const clampWidth = width => Math.round(Math.min(
    Math.max(width, remToPx(MIN_WIDTH_REM)),
    Math.min(remToPx(MAX_WIDTH_REM), window.innerWidth * 0.7)
));

const readStoredWidth = () => {
    try {
        const stored = parseInt(localStorage.getItem(WIDTH_STORAGE_KEY), 10);
        return Number.isFinite(stored) ? clampWidth(stored) : null;
    }
    catch (e) {
        return null;
    }
};

const storeWidth = width => {
    try {
        if (width) localStorage.setItem(WIDTH_STORAGE_KEY, String(width));
        else localStorage.removeItem(WIDTH_STORAGE_KEY);
    }
    catch (e) {
        // storage can be unavailable (private mode); the default width still applies
    }
};

/**
 * AIChat
 * The Insight AI drawer, opened from the top nav (AIChatHeaderButton).
 * On mobile it slides up from the bottom.
 * Rendered once at app level so it doesn't remount on page navigation.
 */

const AIChat = () => {
    const { user } = useContext(UserContext);
    const { activeFarm } = useContext(ActiveFarmContext) || {};
    const aiChat = useAIChat();
    const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY);

    const drawerRef = useRef(null);
    const inputRef = useRef(null);
    const returnFocusRef = useRef(null);
    // set when an outside press closes the drawer, so focus stays where the user pressed
    const skipFocusReturnRef = useRef(false);

    // null = default width (from the stylesheet)
    const [width, setWidth] = useState(readStoredWidth);
    const [isResizing, setIsResizing] = useState(false);

    const isOpen = !!aiChat?.isOpen;
    const closeChat = aiChat?.closeChat;
    const syncSession = aiChat?.syncSession;

    // The chat context sits above the User and ActiveFarm providers, so it's told here
    // who is signed in and which farm questions (and their cost) belong to.
    const userId = user?.id ?? null;
    const farmId = activeFarm ? String(activeFarm) : null;
    useEffect(() => {
        syncSession?.({ userId, farmId });
    }, [syncSession, userId, farmId]);

    // Focus moves into the drawer on open and back to where it came from on close.
    const wasOpenRef = useRef(false);
    useEffect(() => {
        if (isOpen) {
            wasOpenRef.current = true;
            returnFocusRef.current = document.activeElement;
            const timer = setTimeout(() => inputRef.current?.focus(), 60);
            return () => clearTimeout(timer);
        }
        if (!wasOpenRef.current) return undefined;
        wasOpenRef.current = false;
        if (skipFocusReturnRef.current) {
            skipFocusReturnRef.current = false;
        }
        else if (drawerRef.current?.contains(document.activeElement) || document.activeElement === document.body) {
            if (returnFocusRef.current?.isConnected) returnFocusRef.current.focus?.();
        }
        returnFocusRef.current = null;
        return undefined;
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return undefined;
        const onKeyDown = e => {
            // On desktop only when focus is inside, so Escape on a menu opened from the
            // drawer (rendered in its own portal) closes just that menu.
            // On mobile the drawer covers the page.
            if (e.key === 'Escape' && (isMobile || drawerRef.current?.contains(document.activeElement))) {
                closeChat();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [isOpen, isMobile, closeChat]);

    // Desktop: a press outside closes the drawer (mobile uses the backdrop). pointerdown, not
    // click, so a resize drag that ends outside doesn't count; capture phase so the target is
    // still attached when a menu item unmounts on click.
    useEffect(() => {
        if (!isOpen || isMobile) return undefined;
        const onPointerDown = e => {
            const { target } = e;
            if (drawerRef.current?.contains(target)) return;
            // the header button toggles the drawer itself; closing here would make its click reopen it
            if (target.closest?.('.ai-chat-trigger')) return;
            // menus, selects, toasts and alerts opened from the drawer render in their own portals
            if (target.closest?.('#popup-menu-root, #single-select-popup-root, #toast-root, .custom-alert')) return;
            skipFocusReturnRef.current = true;
            closeChat();
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [isOpen, isMobile, closeChat]);

    const startResize = useCallback(e => {
        e.preventDefault();
        const handle = e.currentTarget;
        handle.setPointerCapture?.(e.pointerId);
        setIsResizing(true);

        let latestWidth = null;
        const onMove = moveEvent => {
            latestWidth = clampWidth(window.innerWidth - moveEvent.clientX);
            setWidth(latestWidth);
        };
        const onUp = () => {
            handle.removeEventListener('pointermove', onMove);
            handle.removeEventListener('pointerup', onUp);
            handle.removeEventListener('pointercancel', onUp);
            setIsResizing(false);
            if (latestWidth) storeWidth(latestWidth);
        };
        handle.addEventListener('pointermove', onMove);
        handle.addEventListener('pointerup', onUp);
        handle.addEventListener('pointercancel', onUp);
    }, []);

    const resizeWithKeyboard = e => {
        if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
        e.preventDefault();
        const current = drawerRef.current?.getBoundingClientRect().width || remToPx(DEFAULT_WIDTH_REM);
        const next = clampWidth(current + (e.key === 'ArrowLeft' ? KEYBOARD_RESIZE_STEP : -KEYBOARD_RESIZE_STEP));
        setWidth(next);
        storeWidth(next);
    };

    const resetWidth = () => {
        setWidth(null);
        storeWidth(null);
    };

    if (!aiChat || !canUseAIChat(user)) return null;

    const mount = document.getElementById('portal-root') || document.body;

    return createPortal(
        <>
            {isMobile && (
                // The drawer's close button and Escape cover keyboard users.
                // eslint-disable-next-line max-len
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                <div
                    className={`ai-chat-backdrop ${isOpen ? 'ai-chat-backdrop__visible' : ''}`}
                    onClick={closeChat} />
            )}

            <section
                id="ai-chat-drawer"
                ref={drawerRef}
                className={`ai-chat ${isOpen ? 'ai-chat__open' : ''} ${isResizing ? 'ai-chat__resizing' : ''}`}
                style={!isMobile && width ? { width: `${width}px` } : undefined}
                role="dialog"
                aria-modal={isMobile}
                aria-labelledby="ai-chat-title">
                {!isMobile && (
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
                    <div
                        className="ai-chat_resize-handle"
                        role="separator"
                        aria-orientation="vertical"
                        aria-label="Resize Insight AI panel"
                        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                        tabIndex={0}
                        title="Drag to resize, double-click to reset"
                        onPointerDown={startResize}
                        onDoubleClick={resetWidth}
                        onKeyDown={resizeWithKeyboard} />
                )}

                <header className="ai-chat_header">
                    <h2 id="ai-chat-title" className="ai-chat_header_title">
                        <span className="ai-chat_header_badge" aria-hidden="true">
                            <i className="zelp-ai-icon" />
                        </span>
                        Insight AI
                    </h2>
                    <div className="ai-chat_header_actions">
                        <button
                            type="button"
                            className="ai-chat_icon-button"
                            aria-label="Close Insight AI"
                            title="Close"
                            onClick={closeChat}>
                            <i className="zelpicon zelpicon-close" aria-hidden="true" />
                        </button>
                    </div>
                </header>

                <AIChatMessages />
                <AIChatComposer inputRef={inputRef} />
            </section>
        </>,
        mount
    );
};

export default AIChat;
